import {
  isRegistryBlockType,
  resolveBlockTypeForPresentation,
  type PlatformVerticalKey,
  type RegistryBlockKey,
  type RegistryBlockType,
} from '@web-studio/shared-schemas/catalog';
import { LAYOUT_COMPONENT_TYPE, type LayoutComponentType } from '@web-studio/shared-schemas/content';
import {
  COMPONENT_TYPE_REGISTRY_KEYS,
  PLATFORM_VERTICAL_MATRIX,
} from '@web-studio/shared-schemas/matrix';

/** Component types with a React implementation in `@web-studio/core-components`. */
export const IMPLEMENTED_COMPONENT_TYPES = [
  LAYOUT_COMPONENT_TYPE.navigationHeader,
  LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  LAYOUT_COMPONENT_TYPE.dynamicMenuFlow,
  LAYOUT_COMPONENT_TYPE.reservationFormEmbed,
  LAYOUT_COMPONENT_TYPE.reservationThirdPartyBook,
  LAYOUT_COMPONENT_TYPE.reservationMessengerBook,
  LAYOUT_COMPONENT_TYPE.reservationCallBook,
  LAYOUT_COMPONENT_TYPE.chefStoryBlock,
  LAYOUT_COMPONENT_TYPE.kitchenTeamBlock,
  LAYOUT_COMPONENT_TYPE.imageGallery,
  LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline,
  LAYOUT_COMPONENT_TYPE.allergyCaptureSecureForm,
  LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere,
  LAYOUT_COMPONENT_TYPE.footerStandard,
  LAYOUT_COMPONENT_TYPE.legalImprintBlock,
  LAYOUT_COMPONENT_TYPE.legalPrivacyBlock,
  LAYOUT_COMPONENT_TYPE.simpleOpeningHours,
  LAYOUT_COMPONENT_TYPE.addressMapBlock,
  LAYOUT_COMPONENT_TYPE.drinksListStatic,
  LAYOUT_COMPONENT_TYPE.staticLocationText,
  LAYOUT_COMPONENT_TYPE.simpleMenuStrip,
  LAYOUT_COMPONENT_TYPE.liveBakeryCounterList,
  LAYOUT_COMPONENT_TYPE.socialFeedGrid,
  LAYOUT_COMPONENT_TYPE.coffeeSubscriptionMatrix,
  LAYOUT_COMPONENT_TYPE.preOrderPickupForm,
  LAYOUT_COMPONENT_TYPE.dynamicCocktailSpotlight,
  LAYOUT_COMPONENT_TYPE.privatePartyInquiryForm,
  LAYOUT_COMPONENT_TYPE.liveTapListSync,
  LAYOUT_COMPONENT_TYPE.vipTableBookingStrip,
  LAYOUT_COMPONENT_TYPE.weeklyScheduleRouteStrip,
  LAYOUT_COMPONENT_TYPE.cateringInquiryForm,
  LAYOUT_COMPONENT_TYPE.liveGpsLocationEmbed,
  LAYOUT_COMPONENT_TYPE.popUpTicketPurchaseCard,
  LAYOUT_COMPONENT_TYPE.contactBlock,
  LAYOUT_COMPONENT_TYPE.faqBlock,
  LAYOUT_COMPONENT_TYPE.backToTopFloat,
  LAYOUT_COMPONENT_TYPE.contactChannelsFloat,
  LAYOUT_COMPONENT_TYPE.utilityBar,
  LAYOUT_COMPONENT_TYPE.announcementBar,
] as const satisfies readonly LayoutComponentType[];

/** Registry `allowedBlocks` types across all verticals and tiers. */
export function collectRegistryBlockKeys(): string[] {
  const keys = new Set<string>();
  for (const vertical of Object.values(PLATFORM_VERTICAL_MATRIX)) {
    for (const tier of Object.values(vertical.tiers)) {
      for (const key of tier.allowedBlocks) {
        keys.add(key);
      }
    }
  }
  return [...keys].sort();
}

/** Verticals where a registry block type (or presentation of that type) appears. */
export function verticalsForRegistryKey(
  registryKey: RegistryBlockKey | RegistryBlockType,
): PlatformVerticalKey[] {
  const blockType = isRegistryBlockType(registryKey)
    ? registryKey
    : resolveBlockTypeForPresentation(registryKey);
  if (!blockType) {
    return [];
  }
  const verticals: PlatformVerticalKey[] = [];
  for (const [verticalKey, definition] of Object.entries(PLATFORM_VERTICAL_MATRIX)) {
    const used = Object.values(definition.tiers).some((tier) =>
      tier.allowedBlocks.includes(blockType),
    );
    if (used) {
      verticals.push(verticalKey as PlatformVerticalKey);
    }
  }
  return verticals;
}

/** Implemented canvas types and their registry presentation key. */
export function implementedBlockCatalog(): {
  componentType: LayoutComponentType;
  registryKey: string;
  verticals: PlatformVerticalKey[];
}[] {
  return IMPLEMENTED_COMPONENT_TYPES.map((componentType) => {
    const registryKey = COMPONENT_TYPE_REGISTRY_KEYS[componentType] ?? componentType;
    return {
      componentType,
      registryKey,
      verticals: verticalsForRegistryKey(registryKey),
    };
  });
}

/** Registry-only block types (planned, no React renderer yet). */
export function registryOnlyBlockKeys(): string[] {
  const implementedTypes = new Set<RegistryBlockType>();
  for (const componentType of IMPLEMENTED_COMPONENT_TYPES) {
    const presentation = COMPONENT_TYPE_REGISTRY_KEYS[componentType];
    const blockType = resolveBlockTypeForPresentation(presentation);
    if (blockType) {
      implementedTypes.add(blockType);
    }
  }
  return collectRegistryBlockKeys().filter(
    (key) => !implementedTypes.has(key as RegistryBlockType),
  );
}
